import { Link } from 'react-router-dom';
import { usePortal } from '../lib/portal';
import { CONTACT } from '../lib/contact';
import { AgentPanel } from './AgentPanel';
import { WhatsAppButton } from './WhatsAppButton';
import { PHOTO_CREDITS, PHOTO_SOURCE } from '../lib/credits';
import '../styles/footer.css';

/**
 * The public site footer, shared by the landing page and every marketing page
 * (About, Solutions, Request a demo). Link groups are data, so adding a
 * destination is a one-line change and nothing is hard-wired into the markup.
 * Contact details themselves live in lib/contact.ts — see there for why.
 */

type Item = { label: string; to?: string; href?: string; newTab?: boolean };
const COLUMNS: { title: string; items: Item[] }[] = [
  {
    title: 'Quick Links',
    items: [
      { label: 'About', to: '/about' },
      { label: 'Solutions', to: '/solutions' },
      { label: 'Pricing', href: '/#pricing' },
      { label: 'Find Your Next Home', to: '/homes' },
      { label: 'Request Demo', to: '/demo' },
      { label: 'Login', to: '/login' },
      { label: 'Register', to: '/register' },
    ],
  },
  {
    title: 'Support',
    items: [
      // One sign-in door for every role — the same reasoning Login.tsx itself
      // states: the role decides which room, so both portals point at it
      // rather than at two separate, and otherwise identical, screens.
      { label: 'Landlord Portal', to: '/login' },
      { label: 'Tenant Portal', to: '/login' },
      // Neither a help-centre nor a service-marketplace page exists yet (the
      // marketplace is a named Phase 2 module, not built). Both route to
      // WhatsApp rather than to a dead link — exactly the channel this whole
      // brief exists to surface, and the honest stand-in until each has a
      // real destination.
      { label: 'Help / Support', href: CONTACT.whatsapp },
      { label: 'Service Providers', href: CONTACT.whatsapp },
      // The brochure is a PDF uploaded from the Control Centre and served from
      // the database at this path, so it is a plain relative href (not a
      // React-Router route) opened in a new tab — see the newTab handling below.
      { label: 'Brochure (PDF)', href: '/nyumbasuite-user-guide.pdf', newTab: true },
    ],
  },
];

function Social() {
  const I = ({ href, label, children }: { href: string; label: string; children: React.ReactNode }) => (
    <a className="ft-social" href={href} aria-label={label} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">
      {children}
    </a>
  );
  return (
    <div className="ft-socials">
      <I href={CONTACT.whatsapp} label="WhatsApp">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2Zm0 18a8 8 0 0 1-4-1.1l-.3-.2-2.8.8.8-2.7-.2-.3A8 8 0 1 1 12 20Zm4.4-6c-.2-.1-1.4-.7-1.6-.8s-.4-.1-.5.1-.6.8-.8 1-.3.1-.5 0a6.5 6.5 0 0 1-1.9-1.2 7.2 7.2 0 0 1-1.3-1.7c-.1-.2 0-.4.1-.5l.4-.4.2-.4v-.4l-.8-1.8c-.2-.5-.4-.4-.5-.4h-.5a.9.9 0 0 0-.7.3A2.8 2.8 0 0 0 6 8.9a4.9 4.9 0 0 0 1 2.6 11 11 0 0 0 4.3 3.8c2 .8 2 .5 2.4.5a2.5 2.5 0 0 0 1.6-1.1 2 2 0 0 0 .1-1.1c0-.1-.2-.1-.4-.2Z"/></svg>
      </I>
      <I href={CONTACT.tiktok} label="TikTok">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16.6 2h-3.2v13.4a3 3 0 1 1-2.4-2.9v-3.3a6.3 6.3 0 1 0 5.6 6.2V8.9a7.7 7.7 0 0 0 4.5 1.4V7.1a4.5 4.5 0 0 1-4.5-4.5V2Z"/></svg>
      </I>
      <I href={CONTACT.instagram} label="Instagram">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
      </I>
      <I href={CONTACT.facebook} label="Facebook">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.3-1.5 1.6-1.5h1.6V4a22 22 0 0 0-2.4-.1c-2.4 0-4 1.4-4 4V10H7.6v3h2.3v8h3.6Z"/></svg>
      </I>
      <I href={CONTACT.x} label="X">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.5 3h3l-6.6 7.5L21.7 21h-6l-4.7-6-5.4 6H2.5l7-8L2 3h6.2l4.3 5.6L17.5 3Zm-1 16h1.7L7.6 4.8H5.8L16.5 19Z"/></svg>
      </I>
      <I href={CONTACT.linkedin} label="LinkedIn">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M4.98 3.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5ZM3 9h4v12H3V9Zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.4c0-1.3 0-2.95-1.8-2.95-1.8 0-2.08 1.4-2.08 2.85V21h-4V9Z"/></svg>
      </I>
    </div>
  );
}

export function SiteFooter() {
  const { isAuthenticated, appHome, appLabel, siteHome: home } = usePortal();

  // Signed in, "/" is the app. Every footer link that meant the marketing
  // homepage — the brand, and the two anchors into its sections — has to say
  // /home instead, or it lands on the dashboard with a hash that means
  // nothing there.
  const columns = COLUMNS.map(col => ({
    ...col,
    items: col.items
      .filter(i => !(isAuthenticated && (i.to === '/login' || i.to === '/register')))
      .map(i => (i.href?.startsWith('/#') ? { ...i, href: `${home}${i.href.slice(1)}` } : i))
      .concat(
        isAuthenticated && col.title === 'Quick Links'
          ? [{ label: appLabel, to: appHome }]
          : []),
  }));

  return (
    <footer className="ft">
      <div className="ft-inner">
        <div className="ft-brand-col">
          <Link to={home} className="ft-brand">
            <img src="/brand-mark-64.png?v=2" alt="" width={28} height={28} className="brand-mark" />
            NyumbaSuite
          </Link>
          <p className="ft-desc">
            One platform. AI Agents with a Personal Assistant. Smarter property
            management. Manage properties, tenants, finances, maintenance and
            operations with intelligent AI working behind the scenes.
          </p>
          <Social />
        </div>

        <div className="ft-cols">
          <nav id="contact" className="ft-col" aria-label="Contact NyumbaSuite">
            <h3 className="ft-col-title">Contact NyumbaSuite</h3>
            <ul>
              <li>Phone: <a href={CONTACT.primaryTel}>{CONTACT.primaryPhone}</a></li>
              <li>Email: <a href={CONTACT.emailTo}>{CONTACT.email}</a></li>
            </ul>
          </nav>

          {columns.map((col) => (
            <nav key={col.title} className="ft-col" aria-label={col.title}>
              <h3 className="ft-col-title">{col.title}</h3>
              <ul>
                {col.items.map((i) => (
                  <li key={i.label}>
                    {i.to
                      ? <Link to={i.to}>{i.label}</Link>
                      : <a href={i.href} target={i.newTab || i.href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{i.label}</a>}
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          <nav className="ft-col" aria-label="Social">
            <h3 className="ft-col-title">Social</h3>
            <ul>
              <li><a href={CONTACT.whatsapp} target="_blank" rel="noreferrer">WhatsApp</a></li>
              <li><a href={CONTACT.tiktok} target={CONTACT.tiktok.startsWith('http') ? '_blank' : undefined} rel="noreferrer">TikTok</a></li>
              <li><a href={CONTACT.facebook} target={CONTACT.facebook.startsWith('http') ? '_blank' : undefined} rel="noreferrer">Facebook</a></li>
              <li><a href={CONTACT.instagram} target={CONTACT.instagram.startsWith('http') ? '_blank' : undefined} rel="noreferrer">Instagram</a></li>
              <li><a href={CONTACT.x} target={CONTACT.x.startsWith('http') ? '_blank' : undefined} rel="noreferrer">X</a></li>
              <li><a href={CONTACT.linkedin} target={CONTACT.linkedin.startsWith('http') ? '_blank' : undefined} rel="noreferrer">LinkedIn</a></li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Attribution, when there is anybody to attribute.
          Empty now that every cover is NyumbaSuite's own, so this renders
          nothing — kept because the requirement returns the moment a licensed
          photograph does, and blog-covers.mjs would refill the list.
          When it shows, it sits in the footer of every page rather than on a
          credits page somebody has to find, and each name links to the
          photographer's own page, because that is what the licence asks.
          The list describes what the DEPLOYED site serves, and covers live in
          the database — so emptying it is the LAST step of swapping the covers,
          after the new ones are live, never before. */}
      {PHOTO_CREDITS.length > 0 && (
        <p className="ft-credits">
          Photography by{' '}
          {PHOTO_CREDITS.map((c, i) => (
            <span key={c.profile}>
              {i > 0 && (i === PHOTO_CREDITS.length - 1 ? ' and ' : ', ')}
              <a href={c.profile} target="_blank" rel="noreferrer">{c.name}</a>
            </span>
          ))}
          {' '}on{' '}
          <a href={PHOTO_SOURCE.url} target="_blank" rel="noreferrer">{PHOTO_SOURCE.name}</a>.
        </p>
      )}

      <div className="ft-bar">
        <span>
          © {new Date().getFullYear()} NyumbaSuite. All rights reserved.
          {' · '}<Link to="/privacy">Privacy Policy</Link>
          {' · '}<Link to="/terms">Terms of Service</Link>
          {' · '}<Link to="/privacy#odpc">Data Protection (ODPC)</Link>
          {' · '}<Link to="/delete-account">Delete your account</Link>
        </span>
        <span className="ft-bar-right">
          <span className="ft-status"><span className="ft-dot" />All systems operational</span>
          <span>Built for Kenya · East Africa</span>
        </span>
      </div>

      {/* The public assistant and the WhatsApp entry point. Mounted here
          because the footer is the one component every public page renders
          exactly once, and because a fixed-position element declared after
          the main content is where a screen reader should meet it. */}
      <AgentPanel mode="public" />
      <WhatsAppButton />
    </footer>
  );
}
